import { useState, type ElementType, type MouseEvent, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useUser, useClerk } from '@clerk/react';
import { CreditCard, LogOut, Settings, User as UserIcon } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '../avatar/avatar.js';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
} from '../popover/popover.js';
import { QuickSettingsDialog } from './quick-settings-dialog.js';

function getInitial(
  firstName: string | null | undefined,
  emailAddress: string | undefined,
): string {
  const source = firstName ?? emailAddress ?? '?';
  return source.trim().charAt(0).toUpperCase() || '?';
}

export function UserPopover() {
  const { t } = useTranslation('account');
  const { user, isLoaded } = useUser();
  const { signOut } = useClerk();
  const [quickSettingsOpen, setQuickSettingsOpen] = useState(false);

  if (!isLoaded || !user) return null;

  const email = user.primaryEmailAddress?.emailAddress;
  const displayName =
    [user.firstName, user.lastName].filter(Boolean).join(' ') ||
    user.username ||
    email ||
    t('userMenu.defaultName');
  const initial = getInitial(user.firstName, email);
  const imageUrl = user.imageUrl;

  async function handleSignOut(closePopover: () => void) {
    closePopover();
    try {
      await signOut({ redirectUrl: '/' });
    } catch {
      window.location.href = '/';
    }
  }

  return (
    <>
      <Popover>
        <PopoverTrigger
          type="button"
          aria-label={t('userMenu.ariaLabel', { name: displayName })}
          data-testid="user-popover-trigger"
          className="flex h-8 w-8 items-center justify-center rounded-full outline-none transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
        >
          <Avatar className="h-8 w-8 border border-white/10">
            {imageUrl ? <AvatarImage src={imageUrl} alt={displayName} /> : null}
            <AvatarFallback>{initial}</AvatarFallback>
          </Avatar>
        </PopoverTrigger>

        <PopoverContent
          anchor={{ to: 'bottom end', gap: 8 }}
          className="w-64"
          data-testid="user-popover-panel"
          role="menu"
        >
          {
            (({ close }: { close: () => void }) => (
              <>
                <PopoverHeader>
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10 border border-white/10">
                      {imageUrl ? <AvatarImage src={imageUrl} alt={displayName} /> : null}
                      <AvatarFallback>{initial}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-small font-semibold text-primary">
                        {displayName}
                      </p>
                      {email ? (
                        <p className="truncate text-caption text-secondary">{email}</p>
                      ) : null}
                    </div>
                  </div>
                </PopoverHeader>

                <PopoverBody>
                  <MenuLinkItem
                    to="/account"
                    icon={UserIcon}
                    label={t('userMenu.viewProfile')}
                    onSelect={close}
                  />
                  <MenuLinkItem
                    to="/billing"
                    icon={CreditCard}
                    label={t('userMenu.billing')}
                    onSelect={close}
                  />
                  <MenuButtonItem
                    icon={Settings}
                    label={t('userMenu.quickSettings')}
                    onClick={() => {
                      close();
                      setQuickSettingsOpen(true);
                    }}
                  />
                  <div className="my-1 h-px bg-white/[0.06]" role="separator" aria-hidden="true" />
                  <MenuButtonItem
                    icon={LogOut}
                    label={t('userMenu.signOut')}
                    onClick={() => handleSignOut(close)}
                    tone="danger"
                    data-testid="user-popover-sign-out"
                  />
                </PopoverBody>
              </>
            )) as unknown as ReactNode
          }
        </PopoverContent>
      </Popover>

      <QuickSettingsDialog open={quickSettingsOpen} onClose={() => setQuickSettingsOpen(false)} />
    </>
  );
}

interface BaseItemProps {
  icon: ElementType;
  label: string;
  tone?: 'neutral' | 'danger';
}

interface MenuLinkItemProps extends BaseItemProps {
  to: string;
  onSelect?: () => void;
}

function MenuLinkItem({ icon: Icon, label, to, onSelect, tone = 'neutral' }: MenuLinkItemProps) {
  const toneClass =
    tone === 'danger'
      ? 'text-accent-red hover:bg-accent-red/10'
      : 'text-primary hover:bg-white/[0.06]';

  return (
    <Link
      to={to}
      role="menuitem"
      onClick={onSelect}
      className={`flex items-center gap-2 rounded-md px-2 py-2 text-small transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400 ${toneClass}`}
    >
      <Icon className="h-4 w-4 shrink-0 text-secondary" aria-hidden="true" />
      <span className="truncate">{label}</span>
    </Link>
  );
}

interface MenuButtonItemProps extends BaseItemProps {
  onClick: (e: MouseEvent<HTMLButtonElement>) => void;
  'data-testid'?: string;
}

function MenuButtonItem({
  icon: Icon,
  label,
  onClick,
  tone = 'neutral',
  'data-testid': testId,
}: MenuButtonItemProps) {
  const toneClass =
    tone === 'danger'
      ? 'text-accent-red hover:bg-accent-red/10'
      : 'text-primary hover:bg-white/[0.06]';

  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      data-testid={testId}
      className={`flex w-full items-center gap-2 rounded-md px-2 py-2 text-small transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400 ${toneClass}`}
    >
      <Icon
        className={`h-4 w-4 shrink-0 ${tone === 'danger' ? 'text-accent-red' : 'text-secondary'}`}
        aria-hidden="true"
      />
      <span className="truncate text-left">{label}</span>
    </button>
  );
}
